/** Tipos del modulo TurnoGeo - deben reflejar los DTO del backend (apps/api/src/modules/turno-geo/dto.ts). */

/** Las 4 marcaciones del día: entrada, salida a almorzar, regreso del almuerzo y salida. */
export type RecordType = 'ENTRADA' | 'SALIDA_ALMUERZO' | 'ENTRADA_ALMUERZO' | 'SALIDA';

export const RECORD_TYPE_LABEL: Record<RecordType, string> = {
  ENTRADA: 'Entrada',
  SALIDA_ALMUERZO: 'Salida a almorzar',
  ENTRADA_ALMUERZO: 'Regreso del almuerzo',
  SALIDA: 'Salida',
};

export interface Modality {
  id: string;
  code: string;
  name: string;
  sortOrder: number;
  isActive: boolean;
}
export type CreateModalityInput = { code: string; name: string; sortOrder?: number };
export type UpdateModalityInput = Partial<Pick<Modality, 'code' | 'name' | 'sortOrder' | 'isActive'>>;

export interface OfficeNetworkIp {
  id: string;
  /** "ip", "ip/n" (rango IPv4) o "prefijo::/n" (IPv6). */
  ipAddress: string;
}
export interface OfficeNetwork {
  id: string;
  officeName: string;
  ips: OfficeNetworkIp[];
  publicIpCidrFallback: string | null;
  gpsLatitude: number | null;
  gpsLongitude: number | null;
  gpsRadiusMeters: number;
  /** DIRECTA = internet propio de la sede; VPN = la IP es la salida de una VPN corporativa (prueba la red, no el lugar). */
  networkKind: 'DIRECTA' | 'VPN';
  /** false = en esta sede el GPS no sirve: decide solo la IP. */
  validateGps: boolean;
  notes: string | null;
  providerAsn: number | null;
  providerName: string | null;
  facilities: string | null;
  address: string | null;
  neighborhood: string | null;
  commune: string | null;
  boundaries: string | null;
  landmark: string | null;
  city: string | null;
  country: string | null;
  mapSearchUrl: string | null;
  networkEquipment: string | null;
  ipIsFixed: boolean | null;
  isActive: boolean;
}
export type CreateOfficeNetworkInput = {
  officeName: string;
  ips: string[];
  publicIpCidrFallback?: string | null;
  gpsLatitude?: number | null;
  gpsLongitude?: number | null;
  gpsRadiusMeters?: number;
  networkKind?: 'DIRECTA' | 'VPN';
  validateGps?: boolean;
  notes?: string | null;
  providerAsn?: number | null;
  providerName?: string | null;
  facilities?: string | null;
  address?: string | null;
  neighborhood?: string | null;
  commune?: string | null;
  boundaries?: string | null;
  landmark?: string | null;
  city?: string | null;
  country?: string | null;
  mapSearchUrl?: string | null;
  networkEquipment?: string | null;
  ipIsFixed?: boolean | null;
};
export type UpdateOfficeNetworkInput = Partial<CreateOfficeNetworkInput> & { isActive?: boolean };

/** Parametro de ParametrosTurnoGeo (la pantalla Parametros se arma sola con estas filas). */
export interface TurnoGeoParameter {
  code: string;
  category: string;
  label: string;
  description: string | null;
  valueType: 'BOOL' | 'INT' | 'TEXT';
  value: string;
  minValue: number | null;
  maxValue: number | null;
  sortOrder: number;
}

/** Vista tipada de los parametros que necesita cualquier usuario al marcar (GET /configuration). */
export interface TurnoGeoSettings {
  timeZone: string;
  requirePhoto: boolean;
  photoRetentionDays: number;
  requireGpsPermission: boolean;
  defaultGpsRadiusMeters: number;
  minGpsAccuracyMeters: number;
  ipv6PrefixLength: number;
  allowFallbackCidr: boolean;
  requireHomeApproval: boolean;
  probeIpv4Url: string;
  probeIpv6Url: string;
  providerAliases: string;
  // Jornada
  workStartTime: string;
  workEndTime: string;
  lunchStartTime: string;
  lunchEndTime: string;
  lunchMinutes: number;
  lunchMaxMinutes: number;
  /** "1,2,3,4,5" (0 = domingo ... 6 = sábado). */
  workDays: string;
  weeklyHours: number;
  scheduleToleranceMinutes: number;
  maxShiftHours: number;
  clockDriftMinutes: number;
  showAccumulatedHours: boolean;
  pingsMorningCount: number;
  pingsAfternoonCount: number;
  pingResponseMinutes: number;
  openShiftGraceMinutes: number;
  /** Margen (min) de diferencia para pagar horas extras: se pregunta desde (60 - margen) min de exceso. */
  overtimeMarginMinutes: number;
  /** Solo se pagan como extra las horas confirmadas por el empleado y autorizadas por un jefe. */
  overtimeRequireApproval: boolean;
}

export type HomeLocationStatus = 'PENDIENTE' | 'APROBADA' | 'RECHAZADA';
export type LocationKind = 'CASA' | 'OFICINA';
export interface HomeLocation {
  id: string;
  userId: string;
  userFullName: string;
  /** Casa u oficina propia del empleado. */
  locationKind: LocationKind;
  /** null = registrada solo con GPS (datos móviles). */
  publicIpAddress: string | null;
  gpsLatitude: number | null;
  gpsLongitude: number | null;
  gpsAccuracyMeters: number | null;
  gpsRadiusMeters: number;
  status: HomeLocationStatus;
  reviewedBy: string | null;
  reviewedDate: string | null;
  updatedDate: string | null;
}
export type RegisterHomeInput = {
  locationKind?: LocationKind;
  /** false = datos móviles: se guarda solo el GPS, sin IP. */
  saveIp?: boolean;
  gpsLatitude?: number | null;
  gpsLongitude?: number | null;
  gpsAccuracyMeters?: number | null;
};
export type UpdateHomeLocationInput = { publicIpAddress?: string | null; gpsLatitude?: number | null; gpsLongitude?: number | null; gpsRadiusMeters?: number };

/** "Detectar mi IP": lo que el servidor ve de esta conexion. */
export interface MyNetwork {
  ip: string | null;
  family: 4 | 6 | null;
  comparedAs: string | null;
  suggestedEntry: string | null;
  prefixLength: number;
  matchesOffice: { id: string; name: string; networkKind?: 'DIRECTA' | 'VPN'; matchedBy?: 'IP' | 'ASN' } | null;
  home: { id: string; status: HomeLocationStatus; matches: boolean } | null;
  ownOffice: { id: string; status: HomeLocationStatus; matches: boolean } | null;
  /** provider = nombre a mostrar (alias, ej. Claro); providerRegistered = nombre literal del registro de redes. */
  network: { provider: string | null; providerRegistered: string | null; asn: number | null };
}

export type ValidationStatus = 'ok' | 'fail' | 'warn' | 'na';
export interface ValidationCheck {
  code: 'IP' | 'GPS' | 'HORARIO' | 'FOTO';
  label: string;
  status: ValidationStatus;
  detail: string;
  confidence?: 'alta' | 'media' | 'baja';
}

/** Identidad documental (tabla de extension 1 a 1 con el usuario de la Suite). */
export interface UserProfile {
  id: string;
  userId: string;
  userFullName: string;
  documentType: string;
  documentNumber: string;
}
export type UpsertUserProfileInput = { userId: string; documentType: string; documentNumber: string };

/** Horario semanal esperado (Presencial/Remoto por dia), 0=Domingo ... 6=Sabado. */
export interface UserSchedule {
  id: string;
  userId: string;
  dayOfWeek: number;
  modalityId: string;
  modalityCode: string;
  modalityName: string;
}
export type SetUserScheduleInput = { userId: string; dayOfWeek: number; modalityId: string };
export interface BulkAssignScheduleInput {
  userIds: string[];
  dayOfWeek: number;
  modalityId: string;
}

export interface CreateRecordInput {
  recordType: RecordType;
  /** Foto capturada por el navegador, como data URL (image/...;base64,...). Solo obligatoria si RequirePhoto esta prendido. */
  photoDataUrl?: string | null;
  detectedLatitude?: number | null;
  detectedLongitude?: number | null;
  gpsAccuracyMeters?: number | null;
  /** Decision 4: se guarda explícitamente si el usuario negó el permiso de ubicación. */
  locationPermissionDenied?: boolean;
  /** Nota opcional del empleado (máx. 300 caracteres). */
  note?: string | null;
  /** Hora del celular al marcar (ISO): solo sirve para detectar relojes alterados; la hora que vale es la del servidor. */
  deviceTimestamp?: string | null;
  /** El empleado confirmó el aviso "estás X min tarde/temprano". */
  deviationConfirmed?: boolean;
  /** Lo que la persona declara con los botones de Marcar: trabajo remoto u oficina (obligatorio). */
  declaredWorkMode?: DeclaredWorkMode | null;
  /** Oficina (sede) en la que la persona dice estar, cuando declara OFICINA. */
  declaredOfficeNetworkId?: string | null;
}

/** Modo de trabajo que la persona DECLARA al marcar (independiente de la modalidad que detecta el sistema). */
export type DeclaredWorkMode = 'REMOTO' | 'OFICINA';
export const DECLARED_WORK_MODE_LABEL: Record<DeclaredWorkMode, string> = { REMOTO: 'Trabajo remoto', OFICINA: 'En oficina' };

export interface TurnoGeoRecord {
  id: string;
  userId: string;
  userFullName: string;
  recordType: RecordType;
  recordTimestamp: string;
  photoUrl: string | null;
  detectedPublicIp: string | null;
  detectedLatitude: number | null;
  detectedLongitude: number | null;
  gpsAccuracyMeters: number | null;
  locationPermissionDenied: boolean;
  matchedOfficeNetworkId: string | null;
  matchedOfficeNetworkName: string | null;
  modalityId: string;
  modalityCode: string;
  modalityName: string;
  modalityReason: string | null;
  matchedHomeLocationId: string | null;
  validations: ValidationCheck[];
  userDocumentNumber: string | null;
  note: string | null;
  /** Hora real del registro en el servidor; difiere de recordTimestamp en los cierres tardíos. */
  serverTimestamp: string;
  deviceTimestamp: string | null;
  clockDriftSeconds: number | null;
  scheduleDeviationMinutes: number | null;
  deviationConfirmed: boolean;
  isLateClose: boolean;
  isCorrected: boolean;
  originalTimestamp: string | null;
  correctedBy: string | null;
  correctedDate: string | null;
  correctionReason: string | null;
  /** Lo que la persona declaró al marcar; null en marcaciones anteriores al cambio. */
  declaredWorkMode: DeclaredWorkMode | null;
  declaredOfficeNetworkId?: string | null;
  declaredOfficeName?: string | null;
  /** Re-evaluación en vivo con las sedes y ubicaciones de hoy. */
  revalidation?: RecordRevalidation;
}
export interface RecordRevalidation {
  modalityCode: string;
  modalityName: string;
  cumple: boolean;
  motivo: string;
  changed: boolean;
}

export interface ListRecordsFilters {
  userId?: string;
  dateFrom?: string;
  dateTo?: string;
  recordType?: RecordType;
  modalityCode?: string;
  page?: number;
  pageSize?: number;
}

// Excepciones de horario
export interface ScheduleException {
  id: string;
  userId: string;
  userFullName: string;
  userDocumentNumber: string | null;
  /** 0 = domingo ... 6 = sábado. */
  workDays: number[];
  startTime: string;
  endTime: string;
  lunchStartTime: string;
  lunchEndTime: string;
  /** null = usa el parámetro general. */
  lunchMinutes: number | null;
  validFrom: string;
  validTo: string | null;
  reason: string;
}
export type SaveScheduleExceptionInput = Omit<ScheduleException, 'id' | 'userFullName' | 'userDocumentNumber'>;

// Mi día: reloj, secuencia y horario efectivo
export interface MyDayMark {
  id: string;
  type: RecordType;
  at: string;
  isLateClose: boolean;
  isCorrected: boolean;
  declaredWorkMode?: DeclaredWorkMode | null;
  /** La marcación se hizo con GPS (coordenadas detectadas). */
  hasGps?: boolean;
}
export type CycleState = 'SIN_TURNO' | 'TRABAJANDO' | 'EN_ALMUERZO' | 'ALMUERZO_TERMINADO' | 'CERRADO';
export interface MyDay {
  serverNow: string;
  localDate: string;
  dayOfWeek: number;
  timeZone: string;
  schedule: {
    isWorkDay: boolean;
    start: string;
    end: string;
    lunchStart: string;
    lunchEnd: string;
    lunchMinutes: number;
    crossesMidnight: boolean;
    netMinutes: number;
    source: 'GENERAL' | 'EXCEPCION';
    exceptionReason: string | null;
  };
  state: CycleState;
  /** Solo estas marcaciones están habilitadas ahora. */
  allowedNext: RecordType[];
  marks: MyDayMark[];
  clock: { workedSeconds: number; running: boolean; lunchInProgress: boolean; lunchSeconds: number | null; lunchAutoDiscount: boolean; closed: boolean } | null;
  openShift: OpenShiftInfo | null;
  toleranceMinutes: number;
  clockDriftMinutes: number;
  showAccumulatedHours: boolean;
  /** false = el administrador apagó el reloj del día para empleados (el servidor ya no envía el tiempo). */
  showClock?: boolean;
  /** Toque de ubicación que hay que responder ahora (null = ninguno). */
  pendingPing: PendingPing | null;
}

// Cierre tardío y correcciones
/** Turno abierto de la persona, con lo necesario para cerrarlo bien. */
export interface OpenShiftInfo {
  entryAt: string;
  ageMinutes: number;
  /** true = bloquea: hay que cerrarlo antes de volver a marcar. */
  stale: boolean;
  maxShiftHours: number;
  blockReason?: 'MAX_HORAS' | 'NUEVA_JORNADA' | null;
  /** La hora de cierre debe ser POSTERIOR a esta marcación. */
  lastMarkType?: RecordType;
  lastMarkAt?: string;
  scheduledEndAt?: string;
  suggestedCloseAt?: string;
  marks?: { type: RecordType; at: string }[];
}
export interface CloseOpenShiftInput {
  /** Hora real de salida (ISO). */
  closeAt: string;
  note: string;
}
export interface AdminCloseShiftInput extends CloseOpenShiftInput {
  userId: string;
}
export interface CorrectRecordInput {
  recordTimestamp: string;
  reason: string;
}
export interface RecordAuditEntry {
  id: string;
  recordId: string;
  action: 'CORRECCION' | 'CIERRE_TARDIO' | 'CIERRE_ADMIN';
  oldTimestamp: string | null;
  newTimestamp: string | null;
  reason: string;
  changedBy: string;
  changedDate: string;
}
export interface OpenShift {
  userId: string;
  userFullName: string;
  userDocumentNumber: string | null;
  lastRecordType: RecordType;
  lastRecordAt: string;
  entryAt: string;
  ageMinutes: number;
  stale: boolean;
  blockReason?: 'MAX_HORAS' | 'NUEVA_JORNADA' | null;
  lastMarkAt?: string;
  scheduledEndAt?: string;
  suggestedCloseAt?: string;
}

/** Histórico de turnos que quedaron sin cerrar: cierres tardíos y sus correcciones. */
export type ShiftClosureEvent = 'CIERRE_EMPLEADO' | 'CIERRE_ADMIN' | 'CORRECCION_CIERRE';
export const SHIFT_CLOSURE_EVENT_LABEL: Record<ShiftClosureEvent, string> = {
  CIERRE_EMPLEADO: 'Cerrado por el empleado',
  CIERRE_ADMIN: 'Cerrado por un líder',
  CORRECCION_CIERRE: 'Hora de cierre corregida',
};
export interface ShiftClosureLog {
  id: string;
  userId: string;
  userFullName: string;
  userDocumentNumber: string | null;
  entryRecordId: string;
  closureRecordId: string;
  eventType: ShiftClosureEvent;
  blockReason: 'MAX_HORAS' | 'NUEVA_JORNADA' | null;
  entryAt: string;
  lastMarkType: RecordType | null;
  lastMarkAt: string | null;
  cycleMarks: { type: RecordType; at: string }[] | null;
  scheduledEndAt: string | null;
  suggestedCloseAt: string | null;
  previousCloseAt: string | null;
  declaredCloseAt: string;
  openMinutes: number;
  shiftMinutes: number;
  note: string;
  actorName: string;
  actorRole: string | null;
  clientIp: string | null;
  userAgent: string | null;
  createdAt: string;
}
export interface ListShiftClosureFilters {
  userId?: string;
  dateFrom?: string;
  dateTo?: string;
  eventType?: ShiftClosureEvent;
  limit?: number;
}

export interface PagedResult<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

// Registrador (lecturas de red)
export type ReadingGpsStatus = 'OK' | 'DENEGADO' | 'NO_DISPONIBLE';
export type ReadingStability = 'PRIMERA' | 'IGUAL' | 'CAMBIO' | 'SIN_COMPARAR';
export type SaveReadingInput = {
  ipv4Probe?: string | null;
  ipv6Probe?: string | null;
  gpsLatitude?: number | null;
  gpsLongitude?: number | null;
  gpsAccuracyMeters?: number | null;
  gpsStatus?: ReadingGpsStatus;
  clientTimeZone?: string | null;
};
export interface NetworkReading {
  id: string;
  userId: string;
  userFullName: string;
  readingTimestamp: string;
  readingDate: string;
  serverIp: string | null;
  ipv4: string | null;
  ipv4Source: 'SERVIDOR' | 'SONDA' | null;
  ipv6: string | null;
  ipv6Source: 'SERVIDOR' | 'SONDA' | null;
  networkKeyV4: string | null;
  networkKeyV6: string | null;
  provider: string | null;
  providerRegistered: string | null;
  asn: number | null;
  gpsLatitude: number | null;
  gpsLongitude: number | null;
  gpsAccuracyMeters: number | null;
  gpsStatus: ReadingGpsStatus;
  matchKind: 'SEDE' | 'CASA' | 'OFICINA' | 'NINGUNA';
  matchName: string | null;
  clientTimeZone: string | null;
  stability: ReadingStability;
  changedFields: string[];
}
export type ListReadingsFilters = { userId?: string; from?: string; to?: string; limit?: number };

// Toques de ubicación (P2-B)
export type PingSlot = 'MANANA' | 'TARDE';
export type PingStatus = 'PENDIENTE' | 'RESPONDIDO' | 'NO_RESPONDIDO' | 'CANCELADO';
export const PING_STATUS_LABEL: Record<PingStatus, string> = { PENDIENTE: 'Pendiente', RESPONDIDO: 'Respondido', NO_RESPONDIDO: 'Sin respuesta', CANCELADO: 'Cancelado' };
export interface PendingPing {
  id: string;
  slot: PingSlot;
  scheduledAt: string;
  expiresAt: string;
}
export interface RespondPingInput {
  detectedLatitude?: number | null;
  detectedLongitude?: number | null;
  gpsAccuracyMeters?: number | null;
  locationPermissionDenied?: boolean;
}
export interface RespondPingResult {
  inRange: boolean;
  modalityCode: string;
  reason: string;
}
export interface LocationPing {
  id: string;
  userId: string;
  userFullName: string;
  userDocumentNumber: string | null;
  slot: PingSlot;
  seq: number;
  scheduledAt: string;
  expiresAt: string;
  status: PingStatus;
  respondedAt: string | null;
  detectedPublicIp: string | null;
  detectedLatitude: number | null;
  detectedLongitude: number | null;
  gpsAccuracyMeters: number | null;
  locationPermissionDenied: boolean;
  modalityCode: string | null;
  inRange: boolean | null;
  resultReason: string | null;
}
export interface ListPingsFilters {
  userId?: string;
  dateFrom?: string;
  dateTo?: string;
  status?: PingStatus;
  page?: number;
  pageSize?: number;
}

// Inconsistencias por auditar (P2-B)
export type InconsistencySeverity = 'BAJA' | 'MEDIA' | 'ALTA';
export type InconsistencyStatus = 'PENDIENTE' | 'REVISADA' | 'JUSTIFICADA' | 'DESCARTADA';
export interface Inconsistency {
  id: string;
  userId: string;
  userFullName: string;
  userDocumentNumber: string | null;
  code: string;
  codeLabel: string;
  severity: InconsistencySeverity;
  occurredAt: string;
  workDate: string;
  recordId: string | null;
  pingId: string | null;
  description: string;
  evidence: unknown;
  status: InconsistencyStatus;
  reviewedBy: string | null;
  reviewedDate: string | null;
  reviewNote: string | null;
  coveredByNovelty: string | null;
}
export interface ListInconsistenciesFilters {
  userId?: string;
  code?: string;
  status?: string;
  severity?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  pageSize?: number;
}
export interface ReviewInconsistencyInput {
  status: InconsistencyStatus;
  note?: string;
}
export interface InconsistencySummary {
  pending: number;
  byCode: { code: string; label: string; count: number }[];
}

// Novedades (P2-B)
export type NoveltyType = 'INCAPACIDAD' | 'VACACIONES' | 'PERMISO' | 'OLVIDO_CIERRE' | 'OTRA';
export type NoveltyStatus = 'PENDIENTE' | 'APROBADA' | 'RECHAZADA';
export const NOVELTY_TYPE_LABEL: Record<NoveltyType, string> = {
  INCAPACIDAD: 'Incapacidad',
  VACACIONES: 'Vacaciones',
  PERMISO: 'Permiso',
  OLVIDO_CIERRE: 'Olvido de cierre',
  OTRA: 'Otra',
};
export interface Novelty {
  id: string;
  userId: string;
  userFullName: string;
  userDocumentNumber: string | null;
  noveltyType: NoveltyType;
  startDate: string;
  endDate: string;
  note: string | null;
  status: NoveltyStatus;
  reviewedBy: string | null;
  reviewedDate: string | null;
  reviewNote: string | null;
  createdDate: string;
}
export interface SaveNoveltyInput {
  userId?: string;
  noveltyType: NoveltyType;
  startDate: string;
  endDate: string;
  note?: string;
}
export interface ListNoveltiesFilters {
  userId?: string;
  status?: NoveltyStatus;
  noveltyType?: NoveltyType;
}
export interface ReviewNoveltyInput {
  status: 'APROBADA' | 'RECHAZADA';
  note?: string;
}

// Empleados y carga masiva (P2-C)
export interface ImportRowInput {
  item?: number | string | null;
  documentNumber?: string | number | null;
  process?: string | null;
  fullName?: string | null;
  gender?: string | null;
  email?: string | null;
  phone?: string | number | null;
  costCenter?: string | null;
  city?: string | null;
  employeeStatus?: string | null;
  salary?: string | number | null;
  contractType?: string | null;
  expectedIp?: string | null;
  /** Sedes a asignar, por nombre, separadas por ; (opcional). */
  sedes?: string | null;
}
export type ImportAction = 'CREAR' | 'VINCULAR' | 'OMITIR' | 'ERROR';
export interface ImportPreviewRow {
  index: number;
  item: number | null;
  documentNumber: string;
  fullName: string;
  email: string;
  process: string | null;
  action: ImportAction;
  errors: string[];
  warnings: string[];
  existingUserName: string | null;
}
export interface ImportPreview {
  rows: ImportPreviewRow[];
  summary: { total: number; create: number; link: number; skip: number; error: number };
}
export type ImportOutcome = 'CREADO' | 'VINCULADO' | 'OMITIDO' | 'ERROR';
export interface ImportResultRow {
  index: number;
  item: number | null;
  documentNumber: string;
  fullName: string;
  email: string;
  outcome: ImportOutcome;
  message: string;
  initialPassword?: string;
}
export interface Employee {
  id: string;
  userId: string;
  fullName: string;
  email: string | null;
  documentNumber: string | null;
  itemNo: number | null;
  process: string | null;
  costCenter: string | null;
  contractType: string | null;
  city: string | null;
  gender: string | null;
  phone: string | null;
  employeeStatus: string;
  salary: number | null;
  expectedIp: string | null;
  isActive: boolean;
  sedes?: { id: string; name: string }[];
}

export interface CredentialImportResult {
  email: string;
  status: 'GUARDADA' | 'SIN_USUARIO' | 'ERROR';
  message: string;
}

// Etiquetas (mensajes parametrizables) - P2-E
export interface Label {
  id: string;
  code: string;
  category: string;
  title: string;
  body: string;
  variables: string | null;
  isSystem: boolean;
  isActive: boolean;
  sortOrder: number;
}
export interface PublicLabel {
  code: string;
  title: string;
  body: string;
}
export interface SaveLabelInput {
  code?: string;
  category?: string;
  title?: string;
  body?: string;
  variables?: string | null;
  isActive?: boolean;
  sortOrder?: number;
}

// Lugares contra los que se compara el GPS (mapa + mensaje de distancia) - P2-E
export interface LocationContextPlace {
  kind: 'sede' | 'oficina' | 'casa';
  id: string;
  name: string;
  lat: number;
  lon: number;
  radius: number;
  status: HomeLocationStatus | null;
}
export interface LocationContextSede {
  id: string;
  name: string;
  networkKind: 'DIRECTA' | 'VPN';
  validateGps: boolean;
  hasCoords: boolean;
  hasIp: boolean;
}
export interface LocationContext {
  places: LocationContextPlace[];
  /** Sedes configuradas (con o sin coordenadas). Vacio = nadie ha parametrizado oficinas. */
  sedes: LocationContextSede[];
  minGpsAccuracyMeters: number;
  hasReference: boolean;
  requireApproval: boolean;
  /** Oficinas asignadas a la persona. */
  mySedes?: { id: string; name: string }[];
}
export interface AdminSetLocationInput {
  userId: string;
  locationKind: LocationKind;
  gpsLatitude: number;
  gpsLongitude: number;
  gpsRadiusMeters?: number;
}
export type EmployeePatch = Partial<{
  item: number | string | null;
  documentNumber: string | null;
  process: string | null;
  gender: string | null;
  phone: string | null;
  costCenter: string | null;
  city: string | null;
  employeeStatus: string | null;
  salary: number | string | null;
  contractType: string | null;
  expectedIp: string | null;
  sedeIds: string[];
}>;

// Horas, recargos y festivos (P3) -----------------------------------------
export interface Holiday { id: string; date: string; name: string; isActive: boolean }
export interface RateFactor {
  id: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  monthlyHoursDivisor: number;
  nightStartTime: string;
  nightEndTime: string;
  extraDiurnalFactor: number;
  extraNocturnalFactor: number;
  extraDiurnalFestiveFactor: number;
  extraNocturnalFestiveFactor: number;
  nightSurchargeFactor: number;
  festiveWithCompFactor: number;
  festiveWithoutCompFactor: number;
  nightFestiveWithCompFactor: number;
  nightFestiveWithoutCompFactor: number;
  legalReference: string | null;
}
export type SaveRateFactorInput = Partial<Omit<RateFactor, 'id'>>;

export interface HoursBlock { diurnaOrdinaria: number; nocturnaOrdinaria: number; diurnaFestiva: number; nocturnaFestiva: number }
export interface HoursPay { extras: number; recargoNocturno: number; festivos: number; total: number }
export interface HoursSummary {
  netHours: number;
  worked: HoursBlock;
  extra: HoursBlock;
  extraTotal: number;
  /** Extras por decidir (el empleado no ha respondido o el jefe no ha autorizado). */
  extraReviewHours: number;
  nightSurchargeHours: number;
  festiveHours: { diurnal: number; nocturnal: number; total: number };
  pay: HoursPay | null;
}
export type ExtraOutcome = 'NINGUNA' | 'LIBRE' | 'SIN_DECISION' | 'DECLINADA' | 'SOLICITADA' | 'RECHAZADA' | 'AUTORIZADA';
export const EXTRA_OUTCOME_LABEL: Record<ExtraOutcome, string> = {
  NINGUNA: '—', LIBRE: 'Extra', SIN_DECISION: 'Sin confirmar', DECLINADA: 'No hizo extras', SOLICITADA: 'Pendiente del jefe', RECHAZADA: 'Rechazada', AUTORIZADA: 'Autorizada',
};
export interface HoursDayRow extends HoursSummary {
  date: string;
  dayOfWeek: number;
  isFestive: boolean;
  holidayName: string | null;
  entry: string;
  exit: string | null;
  open: boolean;
  lunchMinutes: number;
  lunchAuto: boolean;
  scheduleSource: 'GENERAL' | 'EXCEPCION';
  scheduledHours: number;
  excessHours: number;
  extraOutcome: ExtraOutcome;
}
export interface HoursWeekRow extends HoursSummary {
  weekStart: string;
  weekEnd: string;
  partial: boolean;
  minHours: number;
  deficitHours: number;
  excessHours: number;
  meetsMin: boolean;
  days: number;
  /** Festivos de la semana que bajan el mínimo. */
  holidayDays: number;
  /** Días con vacaciones/incapacidad/permiso aprobado que bajan el mínimo. */
  excusedDays: number;
}
export interface EmployeeHours extends HoursSummary {
  userId: string;
  fullName: string;
  documentNumber: string | null;
  openShifts: number;
  weeksBelowMin: number;
  weeks: HoursWeekRow[];
  days?: HoursDayRow[];
}
export interface HoursReport {
  from: string;
  to: string;
  weeklyHours: number;
  timeZone: string;
  sundayIsFestive: boolean;
  generatedAt: string;
  warnings: string[];
  employees: EmployeeHours[];
}
export interface HoursReportQuery { from: string; to: string; userId?: string; detail?: boolean }
export interface MyWeek { visible: boolean; weekStart: string; weekEnd: string; netHours: number; minHours: number; holidayDays?: number; excusedDays?: number }
export interface ImportMarkRow { email?: string; document?: string; date: string; entry: string; lunchOut?: string; lunchIn?: string; exit?: string }
export interface ImportMarkResult { row: number; status: 'IMPORTADA' | 'DUPLICADA' | 'ERROR'; message: string }

// ---- Horas extras con autorización (027): empleado -> jefe -> Recursos Humanos ----
export type OvertimeStatus = 'DECLINADA' | 'SOLICITADA' | 'APROBADA' | 'RECHAZADA' | 'ENVIADA' | 'PROCESADA';
export const OVERTIME_STATUS_LABEL: Record<OvertimeStatus, string> = {
  DECLINADA: 'No hizo extras', SOLICITADA: 'Por autorizar', APROBADA: 'Autorizada', RECHAZADA: 'Rechazada', ENVIADA: 'Enviada a RR.HH.', PROCESADA: 'Procesada por RR.HH.',
};
export interface OvertimeCandidate {
  workDate: string;
  entry: string;
  exit: string;
  scheduledMinutes: number;
  workedMinutes: number;
  excessMinutes: number;
  proposedMinutes: number;
  promptFromMinutes: number;
  marginMinutes: number;
}
export interface Overtime {
  id: string;
  userId: string;
  userFullName: string;
  documentNumber: string | null;
  workDate: string;
  scheduledMinutes: number;
  workedMinutes: number;
  excessMinutes: number;
  proposedMinutes: number;
  approvedMinutes: number | null;
  status: OvertimeStatus;
  employeeNote: string | null;
  employeeDecidedDate: string;
  reviewedBy: string | null;
  reviewedDate: string | null;
  reviewNote: string | null;
  sentToHrBy: string | null;
  sentToHrDate: string | null;
  hrProcessedBy: string | null;
  hrProcessedDate: string | null;
  hrNote: string | null;
}
export interface OvertimeQuery { status?: string; userId?: string; dateFrom?: string; dateTo?: string }
export interface OvertimeReviewInput { decision: 'APROBADA' | 'RECHAZADA'; approvedMinutes?: number; note?: string }
export const fmtMinutes = (m: number): string => {
  const v = Math.max(0, Math.round(m));
  const h = Math.floor(v / 60);
  const r = v % 60;
  return h === 0 ? `${r} min` : r === 0 ? `${h} h` : `${h} h ${r} min`;
};
